import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Star, Truck, RotateCcw, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { money, errMsg } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useFav } from "@/context/FavoritesContext";
import { useAuth } from "@/context/AuthContext";

function Accordion({ title, children, open: o = false }) {
  const [open, setOpen] = useState(o);
  return (
    <div className="border-b border-neutral-100">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-4">
        <span className="over text-[0.65rem]">{title}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 text-sm text-neutral-600 leading-relaxed space-y-2">{children}</div>}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { has, toggle } = useFav();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [sel, setSel] = useState({});
  const [qty, setQty] = useState(1);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then(({ data }) => {
      setD(data); setActiveImg(0); setSel({}); setQty(1);
    }).catch(() => nav("/catalogue"));
  }, [id, nav]);

  if (!d) return <div className="h-[60vh] flex items-center justify-center"><span className="over text-xs text-neutral-400">Chargement…</span></div>;
  const p = d.product;
  const isBeauty = p.category === "cosmetics";
  const dims = p.variant_options || [];

  const optionValues = (dim) => [...new Set(p.variants.map((v) => v.options?.[dim]).filter(Boolean))];
  const matchVariant = () => {
    if (!dims.length) return p.variants[0];
    return p.variants.find((v) => dims.every((dmn) => v.options?.[dmn] === sel[dmn]));
  };
  const variant = matchVariant();
  const stockFor = (dim, val) => p.variants.filter((v) => v.options?.[dim] === val).reduce((a, v) => a + v.stock, 0);
  const curStock = variant ? variant.stock : 0;
  const price = (variant?.price) || p.sale_price || p.price;

  const addToCart = () => {
    if (dims.length && !dims.every((dmn) => sel[dmn])) { toast.error("Veuillez choisir toutes les options."); return; }
    if (!variant || variant.stock < qty) { toast.error("Stock insuffisant."); return; }
    add({ product_id: p.id, variant_id: variant.id, name: p.name, image: p.images?.[0], creator_name: p.creator_name,
      price, quantity: qty, variant_label: Object.values(variant.options || {}).join(", ") });
    toast.success("Ajouté à votre sélection");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${id}/reviews`, reviewForm);
      toast.success("Avis publié");
      const { data } = await api.get(`/products/${id}`); setD(data); setReviewForm({ rating: 5, comment: "" });
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-8 lg:py-14">
      <nav className="over text-[0.58rem] text-neutral-400 mb-8">
        <Link to="/" className="hover:text-black">Accueil</Link> / <Link to={`/c/${p.category === "cosmetics" ? "cosmetiques" : p.category === "clothing" ? "vetements" : p.category === "jewelry" ? "bijoux" : "decoration"}`} className="hover:text-black">{p.subcategory}</Link> / <span className="text-neutral-600">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex gap-4">
          {p.images?.length > 1 && (
            <div className="flex flex-col gap-3 w-20 shrink-0">
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-[3/4] overflow-hidden border ${activeImg === i ? "border-[#0A0A0A]" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 aspect-[3/4] bg-neutral-100 overflow-hidden" data-testid="pdp-main-image">
            <img src={p.images?.[activeImg]} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Purchase */}
        <div className="lg:py-4">
          <Link to={`/createur/${p.seller_id}`} className="over text-[0.62rem] text-[#D4AF37] hover:underline" data-testid="pdp-creator-link">{p.creator_name}</Link>
          <h1 className="font-display text-4xl lg:text-5xl font-light mt-2">{p.name}</h1>
          <div className="flex items-center gap-4 mt-4">
            <span className={`text-xl ${p.sale_price ? "text-[#E0115F]" : ""}`}>{money(price)}</span>
            {p.sale_price && <span className="text-neutral-400 line-through">{money(p.price)}</span>}
            {p.reviews_count > 0 && <span className="flex items-center gap-1 text-sm text-neutral-500"><Star size={13} className="fill-[#D4AF37] text-[#D4AF37]" /> {p.rating} ({p.reviews_count})</span>}
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            {p.badges?.map((b) => <span key={b} className="over text-[0.55rem] border border-[#D4AF37]/50 px-2.5 py-1">{b}</span>)}
          </div>

          <p className="text-neutral-600 leading-relaxed mt-6">{p.description}</p>

          {/* Variant selectors */}
          {dims.map((dim) => (
            <div key={dim} className="mt-6">
              <span className="over text-[0.6rem] text-neutral-500">{dim === "size" ? "Taille / Format" : dim === "shade" ? "Teinte" : dim}</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {optionValues(dim).map((val) => {
                  const soldOut = stockFor(dim, val) === 0;
                  return (
                    <button key={val} disabled={soldOut} onClick={() => setSel({ ...sel, [dim]: val })}
                      data-testid={`variant-${dim}-${val}`}
                      className={`over text-[0.62rem] px-4 py-2.5 border transition-colors ${sel[dim] === val ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-neutral-200 hover:border-neutral-900"} ${soldOut ? "opacity-30 line-through cursor-not-allowed" : ""}`}>
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Stock indicator */}
          <div className="mt-5 text-sm">
            {(!dims.length || dims.every((dmn) => sel[dmn])) ? (
              curStock > 0 ? <span className={curStock <= 3 ? "text-[#E0115F]" : "text-neutral-500"}>{curStock <= 3 ? `Plus que ${curStock} en stock` : "En stock"}</span>
                : <span className="text-neutral-400">Épuisé</span>
            ) : <span className="text-neutral-400">Sélectionnez vos options</span>}
          </div>

          {/* Qty + actions */}
          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center border border-neutral-200">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3">−</button>
              <span className="px-4 text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3">+</button>
            </div>
            <button onClick={addToCart} disabled={curStock === 0} className="btn btn-dark flex-1" data-testid="pdp-add-to-cart">Ajouter au panier</button>
            <button onClick={() => toggle(p.id)} className="w-12 h-12 border border-neutral-200 flex items-center justify-center" data-testid="pdp-favorite">
              <Heart size={17} className={has(p.id) ? "fill-[#E0115F] text-[#E0115F]" : ""} />
            </button>
          </div>

          <div className="flex gap-6 mt-6 text-xs text-neutral-500">
            <span className="flex items-center gap-2"><Truck size={14} /> Livraison 2-4 jours</span>
            <span className="flex items-center gap-2"><RotateCcw size={14} /> Retours 14 jours</span>
          </div>

          <div className="mt-8">
            <Accordion title="Détails & matières" open>
              {p.attributes?.material && <p><strong>Matière :</strong> {[].concat(p.attributes.material).join(", ")}</p>}
              {p.attributes?.color && <p><strong>Couleur :</strong> {[].concat(p.attributes.color).join(", ")}</p>}
              {p.attributes?.style && <p><strong>Style :</strong> {[].concat(p.attributes.style).join(", ")}</p>}
              {p.dimensions && <p><strong>Dimensions :</strong> {p.dimensions}</p>}
              <p><strong>Créateur :</strong> {p.creator_name}</p>
            </Accordion>
            {isBeauty && p.beauty && (
              <Accordion title="Composition & utilisation">
                {p.attributes?.skin_type && <p><strong>Type de peau :</strong> {[].concat(p.attributes.skin_type).join(", ")}</p>}
                {p.attributes?.finish && <p><strong>Fini :</strong> {[].concat(p.attributes.finish).join(", ")}</p>}
                {p.attributes?.texture && <p><strong>Texture :</strong> {[].concat(p.attributes.texture).join(", ")}</p>}
                {p.attributes?.benefit && <p><strong>Bénéfices :</strong> {[].concat(p.attributes.benefit).join(", ")}</p>}
                {p.beauty.usage && <p><strong>Mode d'emploi :</strong> {p.beauty.usage}</p>}
                {p.beauty.ingredients && <p><strong>Ingrédients :</strong> {p.beauty.ingredients}</p>}
                {p.beauty.inci && <p className="text-xs text-neutral-400"><strong>INCI :</strong> {p.beauty.inci}</p>}
                {p.beauty.precautions && <p><strong>Précautions :</strong> {p.beauty.precautions}</p>}
                {p.beauty.pao && <p><strong>PAO :</strong> {p.beauty.pao}</p>}
                {p.beauty.country && <p><strong>Fabrication :</strong> {p.beauty.country}</p>}
                {p.beauty.allergens && <p><strong>Allergènes :</strong> {p.beauty.allergens}</p>}
              </Accordion>
            )}
            <Accordion title="Livraison & retours">
              <p>Expédition sous {p.prep_time || "2-4 jours"}. Livraison offerte dès 200 €. Retours acceptés sous 14 jours.</p>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-20">
        <h2 className="font-display text-3xl">Avis ({d.reviews.length})</h2>
        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div className="space-y-6">
            {d.reviews.length === 0 && <p className="text-sm text-neutral-500">Aucun avis pour le moment.</p>}
            {d.reviews.map((r) => (
              <div key={r.id} className="border-b border-neutral-100 pb-5">
                <div className="flex items-center gap-2">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} className={i < r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-neutral-200"} />)}
                  <span className="text-sm font-medium ml-2">{r.user_name}</span>
                </div>
                <p className="text-sm text-neutral-600 mt-2">{r.comment}</p>
              </div>
            ))}
          </div>
          {user && user.id ? (
            <form onSubmit={submitReview} className="bg-[#FAFAFA] p-6">
              <span className="over text-[0.62rem]">Laisser un avis</span>
              <div className="flex gap-1 mt-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <button type="button" key={i} onClick={() => setReviewForm({ ...reviewForm, rating: i + 1 })}>
                    <Star size={20} className={i < reviewForm.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-neutral-300"} />
                  </button>
                ))}
              </div>
              <textarea required value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                placeholder="Votre expérience…" className="w-full border border-neutral-200 p-3 text-sm mt-3 h-24" data-testid="review-comment" />
              <button className="btn btn-dark mt-3" data-testid="review-submit">Publier</button>
              <p className="text-xs text-neutral-400 mt-2">Réservé aux clients ayant acheté ce produit.</p>
            </form>
          ) : (
            <div className="bg-[#FAFAFA] p-6 text-sm text-neutral-500">
              <Link to="/connexion" className="link-underline">Connectez-vous</Link> pour laisser un avis.
            </div>
          )}
        </div>
      </section>

      {d.same_creator?.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-3xl mb-8">Autres créations de {p.creator_name}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-8">
            {d.same_creator.map((sp, i) => <ProductCard key={sp.id} product={sp} index={i} />)}
          </div>
        </section>
      )}
      {d.similar?.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-3xl mb-8">Vous aimerez aussi</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-8">
            {d.similar.map((sp, i) => <ProductCard key={sp.id} product={sp} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
